/**
 * Atualização de termos do programa (re-aceite por versão) — HNS-1594, item 1.7.
 *
 * Quando o texto do TCLE muda, sobe-se a "Versão do Termo PDF" do programa e os
 * pacientes que aceitaram a versão anterior precisam aceitar de novo. O motor para
 * isso já existia no backend, mas só era acionável por chamada direta à API — esta
 * tela expõe o mesmo fluxo para a operação.
 *
 * O disparo vai em lotes de 15 por chamada (limite do backend, que gera PDF e envia
 * e-mail/WhatsApp em cada um), então a tela mostra quantos ainda faltam.
 */
import React, { Component } from "react";
import { Card, Row, Col, Form, Button, Alert, Table, Spinner, Badge } from "react-bootstrap";
import { FaEye, FaPaperPlane, FaExclamationTriangle } from "react-icons/fa";
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import api from "../../services/api";

const MySwal = withReactContent(Swal);

const ASSUNTO_PADRAO = 'Atualização do Termo de Consentimento';
const MENSAGEM_PADRAO =
    '<p>Olá,</p>'
    + '<p>O Termo de Consentimento do programa foi atualizado. '
    + 'Para continuar participando, pedimos que leia e aceite a nova versão.</p>';

class AtualizacaoTermosPrograma extends Component {
    constructor(props) {
        super(props);
        this.state = {
            etapas: [],
            etapa_id: '',
            atendimentos_ids: '',
            email_assunto: ASSUNTO_PADRAO,
            email_mensagem: MENSAGEM_PADRAO,
            atualizar_aceitos: true,
            previa: null,
            carregando: false,
            enviando: false,
            erro: '',
            ultimoEnvio: null,
        };
    }

    componentDidMount() {
        this.carregarEtapas();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.programaId !== this.props.programaId) {
            this.setState({ previa: null, ultimoEnvio: null, etapa_id: '' }, this.carregarEtapas);
        }
    }

    carregarEtapas = async () => {
        const { programaId } = this.props;
        if (!programaId) return;

        try {
            const { data } = await api.get(`/programas/etapas/?programa_id=${programaId}&status=1&limit=100`);
            const etapas = (data.results || data || []).slice().sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
            this.setState({ etapas, etapa_id: this.etapaPadrao(etapas) });
        } catch (e) {
            console.error('Erro ao carregar as etapas do programa:', e);
        }
    };

    /**
     * Quem ainda nao aceitou esta parado na etapa do termo, entao filtrar por ela ajuda.
     * Quem aceitou uma versao anterior ja seguiu a jornada e esta espalhado pelas etapas —
     * nesse caso filtrar por etapa esconderia quase todos.
     */
    etapaPadrao = (etapas = this.state.etapas, atualizarAceitos = this.state.atualizar_aceitos) => {
        if (atualizarAceitos) return '';
        const optin = etapas.find(e => /optin|termo|consentimento/i.test(e.nome || ''));
        return optin ? String(optin.id) : '';
    };

    trocarPublico = (atualizarAceitos) => {
        this.setState({
            atualizar_aceitos: atualizarAceitos,
            etapa_id: this.etapaPadrao(this.state.etapas, atualizarAceitos),
            previa: null,
        });
    };

    montarPayload = (preview) => {
        const { programaId, versao } = this.props;
        const ids = (this.state.atendimentos_ids || '')
            .split(/[^\d]+/).filter(Boolean).join(',');

        return {
            programa_id: programaId,
            etapa_id: this.state.etapa_id || undefined,
            termo_versao: versao || '',
            atualizar_aceitos: this.state.atualizar_aceitos,
            atendimentos_ids: ids || undefined,
            email_assunto: this.state.email_assunto,
            email_mensagem: this.state.email_mensagem,
            preview,
        };
    };

    verPrevia = async () => {
        this.setState({ carregando: true, erro: '', previa: null });

        try {
            const { data } = await api.post('/atendimentos/atualizacao_termos/', this.montarPayload(true));
            this.setState({ previa: data, carregando: false });
        } catch (e) {
            console.error('Erro ao consultar a prévia da atualização de termos:', e);
            this.setState({
                carregando: false,
                erro: e?.response?.data?.detail || 'Não foi possível consultar quem seria notificado.',
            });
        }
    };

    confirmarEnvio = async () => {
        const { versao } = this.props;
        const { previa } = this.state;
        const quantos = previa?.total_atendimentos ?? 0;
        const faltam = previa?.total_pendentes ?? quantos;

        const confirma = await MySwal.fire({
            title: 'Enviar a atualização do termo?',
            html: `<p>Serão notificados agora <strong>${quantos}</strong> paciente(s), `
                + `de um total de <strong>${faltam}</strong> pendente(s).</p>`
                + `<p>Cada um recebe o termo da versão <strong>${versao || '-'}</strong> `
                + `e o aceite anterior vai para o histórico.</p>`
                + `<p class="text-muted" style="font-size:.9em">O envio dispara e-mail (e WhatsApp, se configurado).</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sim, enviar',
            cancelButtonText: 'Cancelar',
        });

        if (!confirma.isConfirmed) return;

        this.setState({ enviando: true, erro: '' });

        try {
            const { data } = await api.post('/atendimentos/atualizacao_termos/', this.montarPayload(false));
            this.setState({ enviando: false, ultimoEnvio: data, previa: null });

            MySwal.fire({
                title: 'Atualização enviada',
                html: `<p><strong>${data.total_atendimentos}</strong> paciente(s) notificado(s).</p>`
                    + (data.total_pendentes > data.total_atendimentos
                        ? `<p>Ainda faltam <strong>${data.total_pendentes - data.total_atendimentos}</strong>. `
                          + `Repita o envio para o próximo lote.</p>`
                        : '<p>Não há mais pendentes nesta versão.</p>'),
                icon: 'success',
            });
        } catch (e) {
            console.error('Erro ao enviar a atualização de termos:', e);
            this.setState({
                enviando: false,
                erro: e?.response?.data?.detail || 'Não foi possível enviar a atualização.',
            });
        }
    };

    render() {
        const { programaId, versao } = this.props;
        const { etapas, etapa_id, email_assunto, email_mensagem, atualizar_aceitos,
                previa, carregando, enviando, erro, ultimoEnvio } = this.state;

        if (!programaId) {
            return (
                <Alert variant="light" className="border mb-0">
                    Salve o programa antes de gerenciar a atualização dos termos.
                </Alert>
            );
        }

        return (
            <Card className="cardDestaque">
                <Card.Body>
                    <h5 className="mb-1">Atualização de Termos (re-aceite)</h5>
                    <p className="text-muted" style={{ fontSize: '13px' }}>
                        Ao mudar o texto do termo, atualize a <strong>Versão do Termo PDF</strong> acima e use esta
                        área para pedir o novo aceite. O aceite anterior de cada paciente fica registrado no histórico.
                    </p>

                    {!versao && (
                        <Alert variant="warning" className="py-2" style={{ fontSize: '13px' }}>
                            <FaExclamationTriangle className="me-1" />
                            O programa está <strong>sem versão de termo</strong> definida. Sem versão não é possível
                            saber quem já aceitou o texto atual — preencha o campo "Versão do Termo PDF" e salve.
                        </Alert>
                    )}

                    <Row className="g-3">
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>Versão atual do termo</Form.Label>
                                <div>
                                    <Badge bg={versao ? 'primary' : 'secondary'} className="fw-normal" style={{ fontSize: '13px' }}>
                                        {versao || 'não definida'}
                                    </Badge>
                                </div>
                            </Form.Group>
                        </Col>

                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>Etapa do termo</Form.Label>
                                <Form.Select
                                    value={etapa_id}
                                    onChange={(e) => this.setState({ etapa_id: e.target.value, previa: null })}
                                >
                                    <option value="">Todas as etapas</option>
                                    {etapas.map(e => (
                                        <option key={e.id} value={e.id}>{e.nome}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>Quem notificar</Form.Label>
                                <Form.Select
                                    value={atualizar_aceitos ? 'aceitos' : 'pendentes'}
                                    onChange={(e) => this.trocarPublico(e.target.value === 'aceitos')}
                                >
                                    <option value="aceitos">Quem aceitou uma versão anterior</option>
                                    <option value="pendentes">Quem ainda não aceitou</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col md={12}>
                            <Form.Group>
                                <Form.Label>Restringir a atendimentos (opcional)</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Ex.: 4518, 4519 — deixe vazio para todos os pendentes"
                                    value={this.state.atendimentos_ids}
                                    onChange={(e) => this.setState({ atendimentos_ids: e.target.value, previa: null })}
                                />
                                <Form.Text className="text-muted">
                                    Útil para reenviar a um paciente específico sem notificar o restante da base.
                                </Form.Text>
                            </Form.Group>
                        </Col>

                        <Col md={12}>
                            <Form.Group>
                                <Form.Label>Assunto do e-mail</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={email_assunto}
                                    onChange={(e) => this.setState({ email_assunto: e.target.value })}
                                />
                            </Form.Group>
                        </Col>

                        <Col md={12}>
                            <Form.Group>
                                <Form.Label>Mensagem do e-mail</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={email_mensagem}
                                    onChange={(e) => this.setState({ email_mensagem: e.target.value })}
                                />
                                <Form.Text className="text-muted">Aceita HTML simples.</Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>

                    <div className="d-flex gap-2 mt-3 flex-wrap">
                        <Button variant="outline-primary" size="sm" onClick={this.verPrevia} disabled={carregando || enviando}>
                            {carregando ? <Spinner animation="border" size="sm" className="me-1" /> : <FaEye className="me-1" />}
                            Ver quem será notificado
                        </Button>

                        <Button
                            variant="primary"
                            size="sm"
                            onClick={this.confirmarEnvio}
                            disabled={!previa || !previa.total_atendimentos || enviando}
                            title={!previa ? 'Consulte a prévia primeiro' : ''}
                        >
                            {enviando ? <Spinner animation="border" size="sm" className="me-1" /> : <FaPaperPlane className="me-1" />}
                            Enviar atualização
                        </Button>
                    </div>

                    {erro && <Alert variant="danger" className="mt-3 py-2" style={{ fontSize: '13px' }}>{erro}</Alert>}

                    {previa && (
                        <div className="mt-3">
                            <Alert variant={previa.total_atendimentos ? 'info' : 'light'} className="py-2 border" style={{ fontSize: '13px' }}>
                                {previa.total_atendimentos
                                    ? <>Serão notificados <strong>{previa.total_atendimentos}</strong> paciente(s) neste
                                       lote, de <strong>{previa.total_pendentes}</strong> pendente(s) no total
                                       (limite de {previa.limite_por_lote} por envio).</>
                                    : <>Nenhum paciente pendente com os filtros atuais.</>}
                            </Alert>

                            {!!previa.enviados?.length && (
                                <Table size="sm" hover responsive className="mb-0" style={{ fontSize: '13px' }}>
                                    <thead>
                                        <tr>
                                            <th>Atendimento</th>
                                            <th>E-mail do destinatário</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {previa.enviados.map(item => (
                                            <tr key={item.id}>
                                                <td>#{item.id}</td>
                                                <td>{item.email}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </div>
                    )}

                    {ultimoEnvio && (
                        <Alert variant="success" className="mt-3 py-2" style={{ fontSize: '13px' }}>
                            Último envio: <strong>{ultimoEnvio.total_atendimentos}</strong> paciente(s) notificado(s)
                            na versão <strong>{ultimoEnvio.versao_programa || versao}</strong>.
                            {ultimoEnvio.total_pendentes > ultimoEnvio.total_atendimentos && (
                                <> Ainda faltam <strong>{ultimoEnvio.total_pendentes - ultimoEnvio.total_atendimentos}</strong>.</>
                            )}
                        </Alert>
                    )}
                </Card.Body>
            </Card>
        );
    }
}

export default AtualizacaoTermosPrograma;
